<template>
  <div class="tts-component" :class="{ multiline: inMultiLine, 'from-se': fromSE }">
    <div class="tts-component-wrapper">
      <div class="tts-audio-wrapper">
        <div class="button-group left">
          <button type="button" class="play action" @click="handlePlayClick">
            <svg-icon :name="playIconName" kind="action" />
          </button>
          <button type="button" class="replay action" @click="handleReplayClick">
            <svg-icon name="Refresh24" kind="action" />
          </button>
        </div>
        <figure class="media" data-type="mo" :data-media-type="'AUDIO'" :data-uuid="audioUuid">
          <inline-loader v-if="isLoading" />
          <text-to-speech-audio-player
            v-else-if="isMediaFileReady"
            ref="ttsAudioPlayer"
            :src="src"
            @media-loaded="mediaLoaded"
            @media-playable="handleMediaPlayable"
            @media-ended="handleMediaEnded"
          />
        </figure>
        <div class="button-group middle">
          <button
            v-if="inMultiLine && isPlaying"
            type="button"
            class="play action"
            @click="handlePlayPrevious"
          >
            <svg-icon name="FastRewind24" kind="action" />
          </button>
          <div v-if="isPlaying" class="wave">
            <span class="bar" />
            <span class="bar" />
            <span class="bar" />
          </div>
          <button
            v-if="inMultiLine && isPlaying"
            type="button"
            class="replay action"
            @click="handlePlayNext"
          >
            <svg-icon name="FastForward24" kind="action" />
          </button>
        </div>
        <div v-if="displayMultiLine" class="button-group right">
          <button
            :disabled="!enableMultiLine"
            type="button"
            class="expand action"
            :class="{ 'disabled-expand': !enableMultiLine }"
            @click="handleExpandClick"
          >
            <svg-icon name="Expand24" kind="action" />
          </button>
        </div>
      </div>
      <div class="tts-inner-content language-chinese-hei">
        <div :id="anchor" class="tts-content-paragraph">
          <template v-if="inMultiLine">
            <inline-loader v-if="isLoading" />
            <rich-text class="multiline" :content="richTextAudio" :expanded="true" />
          </template>
          <template v-else>
            <rich-text :content="audioText" :expanded="true" />
          </template>
        </div>
      </div>
      <attachment-placeholder v-if="showActionTools">
        <template #action-tools>
          <button v-if="showDownload" type="button" class="download action" @click="handleDownload">
            <svg-icon name="Download24" kind="action" />
          </button>
          <button v-if="showTrash" class="trash action">
            <svg-icon name="Trash24" kind="action" />
          </button>
          <button v-if="showSettings" class="settings action" @click="handleSettings">
            <svg-icon name="Settings24" kind="action" />
          </button>
        </template>
      </attachment-placeholder>
    </div>
    <message-component v-if="showUnapprovedMOErrorLabel" class="error submit-validation-error">
      {{ unnapprovedMOErrorLabel }}
    </message-component>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import MediaObjectApi from "@apis/media.object.api";
import { ApiResponse } from "@apis/api.ts";
import TextToSpeechContentSubpage from "@plugins/richtext/plugins/text-to-speech/components/TextToSpeechContentSubpage.vue";
import TextToSpeechAudioPlayer from "@plugins/richtext/plugins/text-to-speech/components/TextToSpeechAudioPlayer.vue";
import TextToSpeechUtils from "@plugins/richtext/plugins/text-to-speech/utils/text.to.speech.utils";
import DOMUtils from "@utils/dom.utils";
import {
  MEDIA_FILE_STATUS,
  MO_DEFAULT_FETCH_INTERVAL,
  MO_FETCH_INTERVAL,
  MEDIA_TYPE,
  RESOURCE_STATUS,
} from "@plugins/media/media.constant";
import { isEmpty, isDefined } from "@utils/var.check";
import ScrollUtils from "@utils/scroll.utils";
import { RESOURCE_DOMAIN } from "@modules/attempt/service/attempt.constant";
import MessageComponent from "@plugins/common/components/MessageComponent.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import AttachmentPlaceholder from "@plugins/common/components/AttachmentPlaceholder.vue";
import RichText from "@plugins/richtext/components/RichText.vue";
import InlineLoader from "@plugins/common/components/InlineLoader.vue";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { useDynamicMountChild } from "@plugins/common/composables/dynamic-mount-child.ts";
import {
  AudioPlayer,
  HighlighterEntry,
  MediaObjectMetadata,
  TTSJson,
} from "@plugins/richtext/types.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { performFileDownload } from "@utils/download.utils.ts";

const props = withDefaults(
  defineProps<{
    inMultiLine?: boolean;
    context?: { pageDomain?: string; lessonTitle?: string };
    audioUuid: string;
    audioText: string;
    isInline?: boolean;
    fromSE?: boolean;
    // eslint-disable-next-line vue/prop-name-casing
    dynamicMountChildMixin_parentComponent?: object;
    // eslint-disable-next-line vue/prop-name-casing
    dynamicMountChildMixin_isDynamicallyMounted?: boolean;
  }>(),
  {
    inMultiLine: false,
    context: undefined,
    isInline: false,
    fromSE: false,
    dynamicMountChildMixin_parentComponent: () => ({}),
    dynamicMountChildMixin_isDynamicallyMounted: false,
  },
);

const emit = defineEmits<{
  (e: "mediaLoaded", id: string): void;
  (
    e: "textToSpeechSettings",
    payload: {
      uuid: string;
      mediaType: string;
      resourceDomain?: string;
      cuePoints: Record<string, unknown>;
    },
  ): void;
}>();

const { t } = useI18n();
const { show: showContentSubpage } = useContentSubpageInstance();
const { actionApiFailHandler } = useErrorHandler();
useDynamicMountChild({
  dynamicMountChildMixin_parentComponent: props.dynamicMountChildMixin_parentComponent,
  dynamicMountChildMixin_isDynamicallyMounted: props.dynamicMountChildMixin_isDynamicallyMounted,
});

const isLoading = ref(true);
const audioMetadata = ref<MediaObjectMetadata>({
  uuid: "",
  fileUrl: "",
  fileName: "",
  fileVersion: "",
});
const audioJson = ref<TTSJson>({ lines: [] }); // for text to speech multiline
const src = ref("");
const sources = ref<string[]>([]);
const player = ref<AudioPlayer>();
const isPlaying = ref(false);
// for tts highlight
const highlighterId = ref<number>(0);
const lineArray = ref<HighlighterEntry[]>([]);
const lineIndex = ref(0);
const enableMultiLine = ref(false);

const displayMultiLine = computed(() => {
  if (isDefined(audioMetadata.value.description)) {
    return audioMetadata.value.description!.includes("sls-tts-multiline") && !props.inMultiLine;
  }
  return false;
});

const showActionTools = computed(() => showDownload.value || showTrash.value || showSettings.value);

// Allow download if
// 1. Is in editor and in view component mode
// 2. media is ready
// 3. Not in subpage/modal
const showDownload = computed(() => {
  const isEditor = isDefined(props.context) ? props.context?.pageDomain === "editor" : false;
  if (
    !isMediaFileReady.value ||
    !isEditor ||
    props.isInline ||
    isMultiline.value ||
    isMediaFileInProcessing.value
  ) {
    return false;
  }
  return !!(audioMetadata.value && audioMetadata.value.downloadable);
});

const showTrash = computed(() => props.isInline);

// Show settings if the media meets the following conditions:
// 1. is inside richtext
// 2. context of page is "editor" (for lesson editor)
const showSettings = computed(() => props.isInline && props.context?.pageDomain === "editor");

const isMultiline = computed(() => props.inMultiLine);

const anchor = computed(() => {
  const randLetter = String.fromCharCode(65 + Math.floor(Math.random() * 26));
  const anchorId = randLetter + Date.now();
  return `tts-${anchorId}`;
});

const playIconName = computed(() => (isPlaying.value ? "Pause24" : "Play24"));

const fileStatus = computed(() => {
  if (!audioMetadata.value || !audioMetadata.value.fileStatus) {
    return "";
  }
  return audioMetadata.value.fileStatus.toUpperCase();
});

const isMediaFileInProcessing = computed(
  () =>
    fileStatus.value === MEDIA_FILE_STATUS.UPLOADING ||
    fileStatus.value === MEDIA_FILE_STATUS.SCANNING ||
    fileStatus.value === MEDIA_FILE_STATUS.PROCESSING,
);

const isMediaFileReady = computed(() => fileStatus.value === MEDIA_FILE_STATUS.READY);

const richTextAudio = computed(() => {
  if (props.inMultiLine && isDefined(audioMetadata.value.description) && !isLoading.value) {
    return processAudioText();
  }
  return "";
});

const unnapprovedMOErrorLabel = computed(() => {
  if (
    RESOURCE_DOMAIN.MOE.toString() === audioMetadata.value.resourceDomain &&
    RESOURCE_STATUS.APPROVED.value !== audioMetadata.value.status &&
    props.context &&
    props.context.pageDomain === "editor"
  ) {
    return t("media_object_not_approve_message");
  }
  return "";
});

const showUnapprovedMOErrorLabel = computed(() => {
  return (
    RESOURCE_DOMAIN.MOE.toString() === audioMetadata.value.resourceDomain &&
    RESOURCE_STATUS.APPROVED.value !== audioMetadata.value.status &&
    props.context &&
    props.context.pageDomain === "editor"
  );
});

function handlePlayClick() {
  if (isPlaying.value) {
    handlePause();
  } else {
    handlePlay();
  }
}

function handlePlay() {
  if (props.inMultiLine) {
    highlight();
    handlePlayByLineIndex(lineIndex.value);
  } else if (player.value) {
    player.value.play();
    isPlaying.value = true;
  }
}

function handlePause() {
  if (player.value) {
    player.value.pause();
    isPlaying.value = false;
  }
}

function handlePlayPrevious() {
  lineIndex.value--;
  if (lineIndex.value <= 0) {
    lineIndex.value = 0;
    handleReplayClick();
    return;
  }
  highlight();
  handlePlayByLineIndex(lineIndex.value);
}

function handlePlayNext() {
  lineIndex.value++;
  if (lineIndex.value > lineArray.value.length - 1) {
    lineIndex.value = lineArray.value.length - 1;
    if (player.value) {
      player.value.restart();
    }
    return;
  }
  highlight();
  handlePlayByLineIndex(lineIndex.value);
}

function handlePlayByLineIndex(index: number) {
  if (!player.value) return;
  player.value.pause();
  isPlaying.value = false;
  player.value.source = {
    type: "audio",
    sources: [{ src: sources.value[index] }],
  };
  // renameSpeedPopUpAndPanelText is a method on child component ref
  const comp = ttsAudioPlayer.value;
  if (
    comp &&
    "renameSpeedPopUpAndPanelText" in comp &&
    typeof comp.renameSpeedPopUpAndPanelText === "function"
  ) {
    comp.renameSpeedPopUpAndPanelText();
  }
  nextTick(() => {
    isPlaying.value = true;
    player.value?.play();
  });
}

function highlight() {
  removeHighlights();
  const entry = lineArray.value[lineIndex.value];
  const idToUse = entry.highlighterId;
  const lineToHighlight = document.getElementById(idToUse.toString());

  if (isDefined(lineToHighlight) && !isEmpty(lineToHighlight?.classList)) {
    lineToHighlight?.classList.add("tts-active");
    setHighlighterId(idToUse);
  }

  //anchor to line
  anchorToLine();
}

function anchorToLine() {
  ScrollUtils.scrollToView(`.tts-active`, {
    block: "center",
    behavior: "smooth",
  });
}

function removeHighlights() {
  const highlighted = document.getElementsByClassName("tts-active")[0] as HTMLElement | undefined;
  if (isDefined(highlighted) && !isEmpty(highlighted?.classList)) {
    highlighted?.classList.remove("tts-active");
  }
}

function setHighlighterId(idToUse: number) {
  highlighterId.value = idToUse;
}

function handleReplayClick() {
  if (props.inMultiLine) {
    lineIndex.value = 0;
    highlight();
    handlePlayByLineIndex(0);
  } else if (player.value) {
    player.value.restart();
    player.value.play();
    isPlaying.value = true;
  }
}

function handleExpandClick() {
  handlePause();
  showContentSubpage(TextToSpeechContentSubpage, {
    baseTitle: [props.context?.lessonTitle],
    audioUuid: props.audioUuid,
    audioText: props.audioText,
  });
}

function processAudioText() {
  const ttsWrapper = DOMUtils.createElement("div", { class: "sls-tts-multiline-wrapper" });
  ttsWrapper.innerHTML = audioMetadata.value.description || "";

  const paragraphs = (ttsWrapper.children[0] as HTMLElement).children as unknown as HTMLElement[];
  const language = TextToSpeechUtils.getLanguage((ttsWrapper.children[0] as HTMLElement).className);

  let highlightIdx = 0;
  (paragraphs as unknown as HTMLElement[]).forEach((paragraph) => {
    if (paragraph.innerText.trim() !== "") {
      // multiline
      const splitText = TextToSpeechUtils.splitTextByDelimiters(
        paragraph.innerText,
        TextToSpeechUtils.getDelimiters(language),
      );

      paragraph.innerText = "";

      splitText.forEach((text) => {
        const childParagraph = DOMUtils.createElement("span", {
          class: "line",
          id: lineArray.value[highlightIdx].highlighterId,
        });
        highlightIdx++;
        childParagraph.innerText = text;
        paragraph.appendChild(childParagraph);
      });
    } else {
      paragraph.innerHTML = "&nbsp;";
    }
  });

  return ttsWrapper.outerHTML;
}

function getMetadata(retryNo: number) {
  MediaObjectApi.getMetaData({ uuid: props.audioUuid }).then(
    (res: ApiResponse<MediaObjectMetadata>) => {
      if (res.code === "error") {
        return;
      }
      audioMetadata.value = res.data;
      // version with timestamp to always retrieve the non-cached/latest version
      src.value = `${audioMetadata.value.fileUrl}?t=${new Date().getTime()}`;
      if (isDefined(props.context)) {
        enableMultiLine.value = !props.isInline || props.context?.pageDomain === "attempt";
      }

      if (isMediaFileInProcessing.value) {
        let interval = MO_DEFAULT_FETCH_INTERVAL;
        if (retryNo < MO_FETCH_INTERVAL.length) {
          interval = MO_FETCH_INTERVAL[retryNo];
        }
        setTimeout(() => {
          getMetadata(retryNo + 1);
        }, interval);
      } else if (props.inMultiLine) {
        getTextToSpeechJson(res.data);
      } else {
        isLoading.value = false;
      }
    },
  );
}

function getTextToSpeechJson(audioData: MediaObjectMetadata) {
  const url = audioData.fileUrl.replace(".mp3", ".json");
  const headers: RequestInit = {
    credentials: "include",
    headers: { Accept: "application/json" },
  };
  fetch(url, headers)
    .then((response) => response.json() as Promise<TTSJson>)
    .then((data) => {
      audioJson.value = data;
      const lines = data.lines;
      const audios: string[] = [];
      const originalUrl = audioData.fileUrl;
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].audioFile) {
          const fileName = originalUrl.replace(".mp3", `_${i}.mp3`);
          audios.push(fileName);
        }
      }
      sources.value = [...audios];
      lineArray.value = lines.map((line, idx) => {
        return {
          uuid: audioData.uuid,
          highlighterId: new Date().getTime() + idx,
          highlighterClass: "highlight-1",
          highlighterSelectors: [{ start: line.start, end: line.end }],
        };
      });
      isLoading.value = false;
      nextTick(() => {
        initHighlighter();
      });
    })
    .catch((err) => console.error(err));
}

function handleMediaPlayable(payload: { player: AudioPlayer; id: string | number | undefined }) {
  player.value = payload.player;
  nextTick(() => {
    emit("mediaLoaded", String(payload.id));
  });
}

function handleMediaEnded() {
  if (props.inMultiLine) {
    if (lineIndex.value < lineArray.value.length - 1) {
      lineIndex.value++;
      highlight();
      handlePlayByLineIndex(lineIndex.value);
    } else {
      removeHighlights();
      lineIndex.value = 0;
      handlePause();
    }
  } else {
    handlePause();
  }
}

function mediaLoaded() {
  const idEmit = `${props.audioUuid}`;
  emit("mediaLoaded", idEmit);
}

function handleDownload(e: MouseEvent) {
  e.preventDefault();
  MediaObjectApi.downloadMediaObject({ uuid: props.audioUuid }).then(
    (response: ApiResponse<string>) => {
      if (response.code === "ok") {
        performFileDownload(response.data);
      }
    },
    actionApiFailHandler,
  );
}

function initHighlighter() {
  const paragraph = document.getElementsByClassName("sls-tts-multiline-wrapper")[0] as
    HTMLElement | undefined;
  if (isDefined(paragraph)) {
    const lines = paragraph?.querySelectorAll("span");
    lines?.forEach((line, idx) => {
      line.addEventListener("click", () => {
        // remove highlight
        paragraph?.querySelectorAll(".tts-active").forEach((highlighted) => {
          highlighted.classList.remove("tts-active");
        });
        // highlight the clicked word
        line.classList.add("tts-active");
        lineIndex.value = idx;
        setHighlighterId(lineArray.value[idx].highlighterId);
        handlePlayByLineIndex(idx);
      });
    });
  }
}

function handleSettings(e: MouseEvent) {
  e.preventDefault();

  const settings = {
    uuid: props.audioUuid,
    mediaType: MEDIA_TYPE.AUDIO,
    resourceDomain: audioMetadata.value.resourceDomain,
    cuePoints: {},
  };

  emit("textToSpeechSettings", settings);
}

const ttsAudioPlayer = ref<InstanceType<typeof TextToSpeechAudioPlayer>>();

onMounted(() => {
  getMetadata(0);
});
</script>
